<template>
  <!--
    «Реквизиты для возврата» (CONCIERGE_CHAT_FIRST.md §5.2, c13): the masked details the operator entered, read from the
    card's state; «Подтвердить реквизиты возврата» sends their version to refund-to/confirm. A replaced payment's card
    says only «Реквизиты заменены»; a refunded one — «Оплата отменена, средства возвращены».
  -->
  <div class="cb" :class="{ 'cb--muted': state?.superseded }" data-testid="chat-block-CONCIERGE_REFUND_TO">
    <div class="cb__title">{{ t('vitrine.concierge.refundTo.title') }}</div>
    <div v-if="state?.superseded" class="cb__state" data-testid="chat-block-state">{{ t(supersededKey(state)) }}</div>
    <template v-else-if="refundTo && lines.length">
      <div data-testid="concierge-refund-to">
        <div v-for="l in lines" :key="l.labelKey" class="cb__row">
          <small>{{ t(l.labelKey) }}</small>
          <span :class="{ num: l.labelKey.endsWith('account') }">{{ l.country ? countryLabel(l.value) : l.value }}</span>
        </div>
      </div>
      <button v-if="!refundTo.confirmed" type="button" class="cb__primary" :disabled="disabled"
              data-testid="concierge-refund-to-confirm" @click="confirm">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
        {{ t('vitrine.concierge.refundTo.confirm') }}
      </button>
      <div v-else class="cb__state" data-testid="concierge-refund-to-confirmed">{{ t('vitrine.concierge.refundTo.confirmed') }}</div>
    </template>
    <p v-else class="cb__text">{{ t('vitrine.concierge.event.REFUND_TO') }}</p>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { confirmConciergeRefundTo } from '@/api/concierge';
import type { BlockState } from '@/api/support';
import { supersededKey } from '../chatBlocks';
import { refundToLines } from '@/views/vitrine/concierge/conciergeUi';
import { useConciergeReference } from '@/views/vitrine/concierge/useConciergeReference';
import { useCardAction } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'CONCIERGE_REFUND_TO' }>;
  /** The concierge request of this thread; null — the screen has not loaded it yet (no call can go). */
  requestId: string | null;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const { countryLabel, loadReference } = useConciergeReference(t);
const { running, error, run } = useCardAction(() => props.reread());

const refundTo = computed(() => (props.state && !props.state.superseded ? props.state.refundTo : null));
const lines = computed(() => refundToLines(refundTo.value));
const disabled = computed(() => !!props.busy || running.value || !props.requestId);

function confirm() {
  const id = props.requestId;
  const r = refundTo.value;
  if (id && r) void run(() => confirmConciergeRefundTo(id, r.version));
}

// The bank's country is a code: its name comes from the country list in the client's language.
onMounted(() => { void loadReference(); });
</script>

<style scoped lang="scss">
@use './cards.scss';
</style>
